/**
 * PartnerDetail - Page détail d'un partner avec tabs
 */

import React, { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery } from '@apollo/client';
import { Title } from 'react-admin';

import {
  Box,
  Typography,
  Paper,
  Chip,
  Grid,
  Card,
  CardContent,
  Tabs,
  Tab,
  CircularProgress,
  Button,
  Alert,
  Divider,
} from '@mui/material';

// Icons
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import PeopleIcon from '@mui/icons-material/People';
import ReceiptIcon from '@mui/icons-material/Receipt';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import SettingsIcon from '@mui/icons-material/Settings';

// Queries
import { GET_ADMIN_PARTNER } from './partners.queries';

import { PartnerDetail as PartnerDetailType, PartnerStatus, PartnerType } from './partners.types';

// Status colors
const STATUS_COLORS: Record<PartnerStatus, string> = {
  [PartnerStatus.PENDING]: '#F59E0B',
  [PartnerStatus.ACTIVE]: '#10B981',
  [PartnerStatus.SUSPENDED]: '#EF4444',
  // PartnerStatus.REJECTED n'existe pas (enum réel : PENDING/ACTIVE/
  // SUSPENDED/TERMINATED, cf partners.types.ts) — TERMINATED manquait ici.
  [PartnerStatus.TERMINATED]: '#6B7280',
};

const TYPE_COLORS: Record<PartnerType, string> = {
  [PartnerType.INSTITUTIONAL]: '#3B82F6',
  [PartnerType.RETAIL]: '#8B5CF6',
  [PartnerType.FEDERATION]: '#EC4899',
};

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;
  return (
    <div role="tabpanel" hidden={value !== index} {...other}>
      {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
    </div>
  );
}

export default function PartnerDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [currentTab, setCurrentTab] = useState(0);

  const { data, loading, error } = useQuery(GET_ADMIN_PARTNER, {
    variables: { id },
    skip: !id,
    fetchPolicy: 'cache-and-network',
  });

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '50vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error || !data?.adminPartner) {
    return (
      <Box>
        <Title title="Partner introuvable" />
        <Alert severity="error">Partner introuvable ou erreur de chargement</Alert>
        <Button startIcon={<ArrowBackIcon />} onClick={() => navigate('/partners')} sx={{ mt: 2 }}>
          Retour à la liste
        </Button>
      </Box>
    );
  }

  const partner: PartnerDetailType = data.adminPartner;

  return (
    <Box>
      <Title title={`Partner: ${partner.name}`} />

      {/* Header */}
      <Box sx={{ mb: 3 }}>
        <Button startIcon={<ArrowBackIcon />} onClick={() => navigate('/partners')} sx={{ mb: 2 }}>
          Retour à la liste
        </Button>

        <Paper sx={{ p: 3 }}>
          <Grid container spacing={3}>
            {/* Logo & Infos principales */}
            <Grid item xs={12} md={8}>
              <Box sx={{ display: 'flex', gap: 3, alignItems: 'start' }}>
                {partner.logoUrl && (
                  <Box
                    component="img"
                    src={partner.logoUrl}
                    alt={partner.name}
                    sx={{ width: 80, height: 80, borderRadius: 2, objectFit: 'cover' }}
                  />
                )}
                <Box sx={{ flex: 1 }}>
                  <Typography variant="h4" fontWeight="bold" gutterBottom>
                    {partner.name}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    {partner.slug}
                  </Typography>
                  {partner.description && (
                    <Typography variant="body2" sx={{ mt: 1 }}>
                      {partner.description}
                    </Typography>
                  )}
                  <Box sx={{ display: 'flex', gap: 1, mt: 2 }}>
                    <Chip
                      label={partner.type}
                      size="small"
                      sx={{ bgcolor: `${TYPE_COLORS[partner.type]}20`, color: TYPE_COLORS[partner.type] }}
                    />
                    <Chip label={partner.plan} size="small" variant="outlined" />
                    <Chip
                      label={partner.status}
                      size="small"
                      sx={{
                        bgcolor: `${STATUS_COLORS[partner.status]}20`,
                        color: STATUS_COLORS[partner.status],
                      }}
                    />
                  </Box>
                </Box>
              </Box>
            </Grid>

            {/* Statistiques clés */}
            <Grid item xs={12} md={4}>
              <Card variant="outlined">
                <CardContent>
                  <Typography variant="overline" color="text.secondary">
                    Statistiques
                  </Typography>
                  <Divider sx={{ my: 1 }} />
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                    <Typography variant="body2">Clients inscrits</Typography>
                    <Typography variant="body2" fontWeight="bold">
                      {partner.enrollmentsCount}
                    </Typography>
                  </Box>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                    <Typography variant="body2">Transactions</Typography>
                    <Typography variant="body2" fontWeight="bold">
                      {partner.transactionsCount}
                    </Typography>
                  </Box>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                    <Typography variant="body2">Revenu</Typography>
                    <Typography variant="body2" fontWeight="bold">
                      {Math.round(partner.totalRevenue / 1000)}k FCFA
                    </Typography>
                  </Box>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                    <Typography variant="body2">Locations</Typography>
                    <Typography variant="body2" fontWeight="bold">
                      {partner.locationsCount}
                    </Typography>
                  </Box>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                    <Typography variant="body2">Staff</Typography>
                    <Typography variant="body2" fontWeight="bold">
                      {partner.staffCount}
                    </Typography>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </Paper>
      </Box>

      {/* Tabs */}
      <Paper>
        <Tabs value={currentTab} onChange={(_, newValue) => setCurrentTab(newValue)}>
          <Tab icon={<TrendingUpIcon />} label="Analytics" />
          <Tab icon={<LocationOnIcon />} label="Locations" />
          <Tab icon={<PeopleIcon />} label="Staff" />
          <Tab icon={<ReceiptIcon />} label="Transactions" />
          <Tab icon={<SettingsIcon />} label="Configuration" />
        </Tabs>

        {/* Tab: Analytics */}
        <TabPanel value={currentTab} index={0}>
          <Typography variant="h6" gutterBottom>
            Analytics du partner
          </Typography>
          <Alert severity="info">
            Les analytics détaillées seront affichées ici (graphiques, tendances, etc.)
          </Alert>
        </TabPanel>

        {/* Tab: Locations */}
        <TabPanel value={currentTab} index={1}>
          <Typography variant="h6" gutterBottom>
            Locations ({partner.locationsCount})
          </Typography>
          <Alert severity="info">
            La liste des locations de ce partner sera affichée ici
          </Alert>
        </TabPanel>

        {/* Tab: Staff */}
        <TabPanel value={currentTab} index={2}>
          <Typography variant="h6" gutterBottom>
            Staff ({partner.staffCount})
          </Typography>
          <Alert severity="info">
            La liste du staff de ce partner sera affichée ici
          </Alert>
        </TabPanel>

        {/* Tab: Transactions */}
        <TabPanel value={currentTab} index={3}>
          <Typography variant="h6" gutterBottom>
            Transactions ({partner.transactionsCount})
          </Typography>
          <Alert severity="info">
            La liste des transactions de ce partner sera affichée ici
          </Alert>
        </TabPanel>

        {/* Tab: Configuration */}
        <TabPanel value={currentTab} index={4}>
          <Typography variant="h6" gutterBottom>
            Configuration du partner
          </Typography>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12} md={6}>
              <Typography variant="caption" color="text.secondary">
                API Key
              </Typography>
              <Typography variant="body2" fontFamily="monospace">
                {partner.apiKey}
              </Typography>
            </Grid>
            <Grid item xs={12} md={6}>
              <Typography variant="caption" color="text.secondary">
                Display Mode
              </Typography>
              <Typography variant="body2">{partner.displayMode}</Typography>
            </Grid>
            {partner.customDomain && (
              <Grid item xs={12} md={6}>
                <Typography variant="caption" color="text.secondary">
                  Domaine personnalisé
                </Typography>
                <Typography variant="body2">{partner.customDomain}</Typography>
              </Grid>
            )}
            {partner.webhookUrl && (
              <Grid item xs={12} md={6}>
                <Typography variant="caption" color="text.secondary">
                  Webhook URL
                </Typography>
                <Typography variant="body2" fontFamily="monospace">
                  {partner.webhookUrl}
                </Typography>
              </Grid>
            )}
            <Grid item xs={12}>
              <Typography variant="caption" color="text.secondary">
                Wallet Config
              </Typography>
              <Paper variant="outlined" sx={{ p: 2, mt: 1 }}>
                <Typography variant="body2" fontFamily="monospace" component="pre">
                  {JSON.stringify(partner.walletConfig, null, 2)}
                </Typography>
              </Paper>
            </Grid>
          </Grid>
        </TabPanel>
      </Paper>
    </Box>
  );
}
